import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { QrCode, Download, FileText } from 'lucide-react';

const ShopQR = () => {
  const { shopId } = useParams();
  const { api } = useAuth();
  const [shop, setShop] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [shopId]);

  const fetchData = async () => {
    try {
      const [shopRes, qrRes] = await Promise.all([
        api.get(`/shops/${shopId}`),
        api.get(`/shops/${shopId}/qr`)
      ]);
      setShop(shopRes.data);
      setQrData(qrRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const downloadQR = () => {
    if (!qrData?.qrCode) return;
    
    const link = document.createElement('a');
    link.href = qrData.qrCode;
    link.download = `qr-${shop?.slug || 'catalogue'}.png`;
    link.click();
    toast.success('QR code téléchargé !');
  };

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await api.get(`/shops/${shopId}/qr/pdf`);
      
      const link = document.createElement('a');
      link.href = response.data.pdf;
      link.download = response.data.filename;
      link.click();
      toast.success('PDF téléchargé !');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="shop-qr">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope']">QR Code</h1>
        <p className="text-muted-foreground mt-1">
          Générez et téléchargez votre QR code
        </p>
      </div>

      {!shop?.isPublished && (
        <Card className="border-yellow-500/50 bg-yellow-50 dark:bg-yellow-950/20">
          <CardContent className="py-4">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              Votre catalogue n'est pas encore publié. Publiez-le dans les paramètres pour que le QR code fonctionne.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>QR Code de votre catalogue</CardTitle>
          <CardDescription>
            Ce QR code redirige vers la page publique de votre catalogue
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* QR Code display */}
          <div className="flex flex-col items-center gap-4">
            {qrData?.qrCode ? (
              <div className="p-4 bg-white rounded-xl shadow-md">
                <img 
                  src={qrData.qrCode} 
                  alt="QR Code" 
                  className="w-64 h-64"
                  data-testid="qr-code-image"
                />
              </div>
            ) : (
              <div className="w-64 h-64 bg-muted rounded-xl flex items-center justify-center">
                <QrCode className="w-16 h-16 text-muted-foreground" />
              </div>
            )}
            
            <p className="text-sm text-muted-foreground text-center break-all max-w-full px-4">
              {qrData?.url}
            </p>
          </div>

          {/* Download buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={downloadQR} data-testid="download-qr-png">
              <Download className="w-4 h-4 mr-2" />
              Télécharger PNG
            </Button>
            <Button variant="outline" onClick={downloadPDF} disabled={downloading} data-testid="download-qr-pdf">
              <FileText className="w-4 h-4 mr-2" />
              {downloading ? 'Génération...' : 'Télécharger PDF A4'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Usage tips */}
      <Card>
        <CardHeader>
          <CardTitle>Comment utiliser votre QR code</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="font-bold text-foreground">1.</span>
              Affichez le QR code en vitrine de votre magasin
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-foreground">2.</span>
              Imprimez-le sur vos cartes de visite ou flyers
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-foreground">3.</span>
              Partagez-le sur vos réseaux sociaux
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-foreground">4.</span>
              Vos clients scannent et accèdent instantanément à votre catalogue
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default ShopQR;
